import React, { FC, useCallback, useState } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import styled from 'styled-components';
import { PrimaryButton } from 'src/components/widgets/buttons';
import { Dialog, DialogWindow, useDialogManager } from 'src/components/widgets/dialog';
import { showErrorNotification } from 'src/components/widgets/notificationToaster/notificationToaster';
import { T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import type { aiCreditsPolicyDialogAcceptMutation } from './__generated__/aiCreditsPolicyDialogAcceptMutation.graphql';
import type { aiCreditsPolicyDialogCreditsQuery } from './__generated__/aiCreditsPolicyDialogCreditsQuery.graphql';
import type { aiCreditsPolicyDialogQuery } from './__generated__/aiCreditsPolicyDialogQuery.graphql';

const AI_CREDITS_POLICY_DIALOG_ID = 'ai-credits-policy';

const PolicyWindow = styled(DialogWindow)`
    width: 540px;
    flex-basis: 540px;
    max-width: 540px;
    border-radius: 16px;
    padding: 28px 28px 24px;
`;

const Title = styled.h2`
    margin: 0 32px 8px 0;
    font-size: 20px;
    font-weight: 700;
    color: ${(props) => props.theme.app.textColor};
`;

const Intro = styled.p`
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.5;
    color: #607d8b;
`;

// The magenta surface of the AI dialogs (the EXTRA banner of the upgrade dialog).
const CreditsBox = styled.div`
    background: ${(props) => props.theme.app.component.notificationBanner.extra.backgroundColor};
    border-radius: 12px;
    padding: 16px 18px;
    font-size: 14px;
    line-height: 1.5;
    color: ${(props) => props.theme.app.textColor};

    ul {
        margin: 10px 0 0;
        padding-left: 18px;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
`;

const CreditsHead = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-weight: 600;
`;

const CreditsValue = styled.span`
    font-size: 22px;
    font-weight: 700;
`;

// Same magenta of the box, as in the other AI dialogs.
const HowItWorksLink = styled.a`
    display: inline-block;
    margin-top: 12px;
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.app.component.notificationBanner.extra.iconContainerBGColor};
    text-decoration: underline;

    &:hover {
        opacity: 0.85;
    }
`;

const AI_CREDITS_HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/31154048227740-AI-Credits-and-Billing';

const AcceptLabel = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 18px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.5;
    color: ${(props) => props.theme.app.textColor};

    input {
        margin-top: 3px;
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        cursor: pointer;
    }
`;

const OwnerNotice = styled.p`
    margin: 18px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: #607d8b;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
`;

const formatCredits = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });

interface AICreditsPolicyDialogProps {
    // The current user can accept: the merchant with the OWNER permission (same rule as acceptAICreditsPolicy)
    canAccept: boolean;
    // Free AI credits left, when they could be read
    freeCreditsLeft: number | null;
    onAccepted: () => void;
    onCancel: () => void;
}

// Closing it (the X, or Esc) cancels the AI feature and accepts nothing; Continue, enabled once the checkbox is
// ticked, accepts the policy and the feature runs. A user who cannot accept can only close it.
const AICreditsPolicyDialog: FC<AICreditsPolicyDialogProps> = ({ canAccept, freeCreditsLeft, onAccepted, onCancel }) => {
    const [checked, setChecked] = useState(false);
    const [commitAccept, isAccepting] = useMutation<aiCreditsPolicyDialogAcceptMutation>(graphql`
        mutation aiCreditsPolicyDialogAcceptMutation {
            acceptAICreditsPolicy {
                id
                aiCreditsPolicyAccepted
            }
        }
    `);

    const showAcceptError = () => {
        showErrorNotification({ message: T._('The AI credits policy could not be accepted. Please try again.', 'Admin') });
    };

    const accept = () => {
        commitAccept({
            variables: {},
            onCompleted: (response, errors) => {
                if (errors?.length || !response.acceptAICreditsPolicy?.aiCreditsPolicyAccepted) {
                    showAcceptError();
                    return;
                }
                onAccepted();
            },
            onError: () => {
                showAcceptError();
            }
        });
    };

    return (
        <Dialog windowDecorator={PolicyWindow} onClose={isAccepting ? () => undefined : onCancel}>
            <Title>{T._('This action uses AI credits', 'Admin')}</Title>
            <Intro>
                {T._(
                    "All Zakeke AI features work with credits. Many operations, like chatting with the assistant, use 0 credits. The action you're about to run uses some.",
                    'Admin'
                )}
            </Intro>
            <CreditsBox>
                <CreditsHead>
                    <span>{T._('Free AI credits left', 'Admin')}</span>
                    {freeCreditsLeft !== null && <CreditsValue>{formatCredits(freeCreditsLeft)}</CreditsValue>}
                </CreditsHead>
                <ul>
                    <li>{T._('Nothing is charged while you have free credits.', 'Admin')}</li>
                    <li>{T._('Your free credits are shared across all Zakeke AI features, in your backoffice and in the Customizer.', 'Admin')}</li>
                    <li>{T._('Once they’re used up, additional usage is billed automatically at the end of your billing period.', 'Admin')}</li>
                    <li>{T._('You can check your usage at any time in Plan Usage.', 'Admin')}</li>
                </ul>
                <HowItWorksLink href={AI_CREDITS_HELP_URL} target='_blank' rel='noopener noreferrer'>
                    {T._('How AI credits work', 'Admin')}
                </HowItWorksLink>
            </CreditsBox>
            {canAccept ? (
                <AcceptLabel>
                    <input type='checkbox' checked={checked} disabled={isAccepting} onChange={(event) => setChecked(event.currentTarget.checked)} />
                    {T._('I understand that once my free credits are used up, AI usage will be billed to my account.', 'Admin')}
                </AcceptLabel>
            ) : (
                <OwnerNotice>
                    {T._('Only the account owner can accept the AI credits policy. Ask the owner to accept it to use this feature.', 'Admin')}
                </OwnerNotice>
            )}
            {canAccept && (
                <Footer>
                    <PrimaryButton type='button' disabled={!checked || isAccepting} onClick={accept}>
                        {T._('Continue', 'Admin')}
                    </PrimaryButton>
                </Footer>
            )}
        </Dialog>
    );
};

/**
 * Before an AI feature of the Backoffice that consumes AI credits: the seller has to accept the AI credits policy
 * once. Resolves true when it is accepted (already, or now in the dialog), false when the user closes the dialog:
 * the feature must not run. When the acceptance cannot be read the feature runs (the backend does not enforce the
 * policy), so an outage of the check never blocks the AI tools.
 */
// How the policy check ended: already accepted (or not readable), accepted now in the dialog, or the dialog closed.
export type AICreditsPolicyOutcome = 'already' | 'accepted' | 'declined';

export function useAICreditsPolicyGate(): {
    ensureAICreditsPolicyAccepted: () => Promise<boolean>;
    askAICreditsPolicy: () => Promise<AICreditsPolicyOutcome>;
} {
    const relayEnvironment = useRelayEnvironment();
    const { showDialog, closeDialog } = useDialogManager();
    const { isAllowed } = usePermissions();

    const askAICreditsPolicy = useCallback(async (): Promise<AICreditsPolicyOutcome> => {
        let accepted: boolean;
        try {
            const data = await fetchQuery<aiCreditsPolicyDialogQuery>(
                relayEnvironment,
                graphql`
                    query aiCreditsPolicyDialogQuery {
                        seller {
                            id
                            aiCreditsPolicyAccepted
                        }
                    }
                `,
                {},
                // Once accepted the store keeps it (the mutation returns the seller): no request for every use.
                { fetchPolicy: 'store-or-network' }
            ).toPromise();
            accepted = data?.seller?.aiCreditsPolicyAccepted ?? true;
        } catch (error) {
            console.error('Failed to read the AI credits policy acceptance:', error);
            return 'already';
        }

        if (accepted) return 'already';

        // The free credits left, fresh: shown in the dialog only when they can be read.
        let freeCreditsLeft: number | null = null;
        const canReadSubscription = isAllowed(['SUBSCRIPTION']);
        if (canReadSubscription) {
            try {
                const credits = await fetchQuery<aiCreditsPolicyDialogCreditsQuery>(
                    relayEnvironment,
                    graphql`
                        query aiCreditsPolicyDialogCreditsQuery {
                            aiCredits {
                                availableFreeCredits
                            }
                        }
                    `,
                    {},
                    { fetchPolicy: 'network-only' }
                ).toPromise();
                freeCreditsLeft = credits?.aiCredits?.availableFreeCredits ?? null;
            } catch (error) {
                console.error('Failed to read the free AI credits:', error);
            }
        }

        return new Promise<AICreditsPolicyOutcome>((resolve) => {
            const finish = (result: AICreditsPolicyOutcome) => {
                closeDialog(AI_CREDITS_POLICY_DIALOG_ID);
                resolve(result);
            };
            showDialog(
                AI_CREDITS_POLICY_DIALOG_ID,
                <AICreditsPolicyDialog
                    canAccept={isAllowed(['OWNER'])}
                    freeCreditsLeft={freeCreditsLeft}
                    onAccepted={() => finish('accepted')}
                    onCancel={() => finish('declined')}
                />
            );
        });
    }, [relayEnvironment, showDialog, closeDialog, isAllowed]);

    const ensureAICreditsPolicyAccepted = useCallback(async (): Promise<boolean> => (await askAICreditsPolicy()) !== 'declined', [askAICreditsPolicy]);

    return { ensureAICreditsPolicyAccepted, askAICreditsPolicy };
}
